import { useQueries, useQuery } from '@tanstack/react-query';
import type { Engagement } from '@ai-secure-twin/shared';
import { api } from './api';

/**
 * Collects a per-engagement resource across the whole tenant.
 *
 * Findings and audit events are keyed by engagement, so a tenant-wide view means one request
 * each. That is fine for the number of engagements an organisation actually runs and would
 * not be at a few hundred — at which point these need their own index rather than a fan-out.
 * Said plainly here because the cost is invisible until it isn't.
 */
export function useAcrossEngagements<T>(
  key: string,
  fetchOne: (engagementId: string) => Promise<{ items: T[] }>,
) {
  const engagements = useQuery({ queryKey: ['engagements'], queryFn: api.listEngagements });
  const list: Engagement[] = engagements.data?.items ?? [];

  const results = useQueries({
    queries: list.map((engagement) => ({
      queryKey: [key, engagement.id],
      queryFn: () => fetchOne(engagement.id),
    })),
  });

  return {
    /** Each item paired with the engagement it belongs to, which the row needs to show. */
    items: results.flatMap((result, i) =>
      (result.data?.items ?? []).map((item) => ({ item, engagement: list[i]! })),
    ),
    isLoading: engagements.isLoading || results.some((r) => r.isLoading),
    isError: engagements.isError,
    error: engagements.error,
    /** True when at least one engagement's fetch failed, so the view is incomplete. */
    isPartial: results.some((r) => r.isError),
    engagementCount: list.length,
  };
}
